import {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {ChangeEvent, KeyboardEvent, MouseEvent, MutableRefObject} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import type {
    ColDef,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
    ValueGetterParams
} from 'ag-grid-community';
import {AppGrid} from 'components/common/ag-grid/AppGrid.component';
import {ConfigurationPage} from 'components/common/configuration/ConfigurationPage.component';
import {
    ACTION_BUTTONS,
    actionsColumn,
    cellOwnsKeysWhileEditing,
    configurationGridHeight,
    EDITING_ACTIONS_WIDTH,
    editButtonId,
    focusWhenDrawn,
    gridColumn,
    isAgGridInConfigurationEnabled,
    ROW_HEIGHT,
    RowActions,
    samePicks,
    tabOutOfTheHeaderIsNotTheGrids,
    useConfirmLeaveWhileEditing,
    useReturnFocusAfterEdit,
    walkRowControls
} from 'components/common/ag-grid/configuration/appConfigurationGrid.function';
import {useAvailableHeight} from 'components/common/ag-grid/appGrid.function';
import {Button, ButtonType} from 'components/common/Button.component';
import Checkbox from 'components/common/checkbox.component';
import {KEY} from 'components/common/constants';
import {useModalContext} from 'components/modals/ModalContext';
import {useTranslation} from 'components/translations/useTranslation';
import {splitNameAndEmail} from 'utils/userFormatter.function';
import type {Translate} from 'types/myTasks';
import type {Department, DepartmentApprover, DepartmentsProps} from 'types/departments';
import type {DepartmentGridRow} from 'types/newDepartments';
import * as reduxSelectors from '../store/application.reducers';
import UserSearch from '../input/UserSearch.component';
import SearchGroupInput from '../input/SearchGroupInput.component';
import Departments from './Departments.component';
import {filterChanged, getDepartments, saveDepartmentChanges} from './departments.action';
import './newDepartments.scss';

// the page, as its class names and ids spell it
const PAGE = 'departments';

// the columns the grid draws, in the order the classic list had them
const NAME_COL = 'name';
const APPROVERS_COL = 'approvers';

// the grid fills the page, keeping the card's own padding and the page's bottom margin below it
const GRID_HEIGHT = configurationGridHeight(PAGE);

// the picker the row being edited puts in its approvers cell, ahead of that row's own buttons
const EDITOR_INPUT = '.new-departments__editor input:not([disabled])';
const ROW_CONTROLS = `${EDITOR_INPUT}, ${ACTION_BUTTONS}`;

/**
 * The list the picker has open, which is drawn over the page rather than in the cell - see the
 * picker below. It is what tells an Escape that closed a list from one meant for the row.
 */
const OPEN_PICKER_LIST = '.ga-select__menu';

/** The id of the row's approver picker, so a label can name it and focus can find it. */
const pickerId = (rowId: string) => `departments-approvers-${rowId}`;

/** The row a control stands in, as ag-grid numbers the rows it has drawn. */
function rowIndexOf(element: HTMLElement): number | null {
    const index = element.closest('.ag-row')?.getAttribute('row-index');
    return index === null || index === undefined ? null : Number(index);
}

/**
 * The next row from `from`, walking in `step`, that has a control to stand on. A department the
 * master data has deleted has nothing left to assign and so carries no Edit button: it is no tab
 * stop and is walked over. Null once there is nothing further that way - which is where focus
 * should leave the grid.
 */
function rowToFocus(api: GridApi<DepartmentGridRow>, from: number, step: number): IRowNode<DepartmentGridRow> | null {
    for (let index = from; index >= 0 && index < api.getDisplayedRowCount(); index += step) {
        const node = api.getDisplayedRowAtIndex(index);
        if (node?.data && !node.data.deleted) {
            return node;
        }
    }
    return null;
}

/** The approvers as the cell reads them while the row is not being edited. */
function approverNames(managers: DepartmentApprover[]): string {
    return managers.map(manager => manager.label).filter(Boolean).join(', ');
}

/**
 * The approvers of a department, as `name (email), name (email)` - the email set apart from the
 * name it belongs to in Gaia's secondary colour, so the two read as the different things they are.
 *
 * The store keeps an approver under one label carrying both, and a placeholder with no label at
 * all for one it could not resolve (see departments.reducer) - which is nothing to list.
 */
const ApproverList = ({managers}: { managers: DepartmentApprover[] }) => (
    <>
        {managers.filter(manager => manager.label).map((manager, index) => {
            const parts = splitNameAndEmail(manager.label);
            return (
                <Fragment key={manager.value ?? manager.label}>
                    {index > 0 && ', '}
                    {/* a label the store kept without an email is read as it stands */}
                    {parts ? parts.name : manager.label}
                    {parts && <span className="text-body-secondary">{` (${parts.email})`}</span>}
                </Fragment>
            );
        })}
    </>
);

/**
 * The rows the grid draws: the departments that have a name, and - only when the page is asked
 * for them - the ones the master data has since deleted.
 */
function buildRowData(departments: Department[], showDeleted: boolean, translate: Translate): DepartmentGridRow[] {
    return departments
        .filter(department => department.name && (!department.mdmDeleted || showDeleted))
        .map(department => {
            const deleted = !!department.mdmDeleted;
            const label = `${department.name} (${department.organizationUnitId})`;
            const managers = department.managers ?? [];

            return {
                id: `${department.internalOrgUnitId}-${department.organizationUnitId}`,
                internalOrgUnitId: department.internalOrgUnitId,
                name: department.name,
                label: deleted ? `${label} ${translate('departments.deleted')}` : label,
                deleted,
                managers,
                approvers: approverNames(managers)
            };
        });
}

/**
 * The picker the row being edited puts in its approvers cell. It keeps the approvers it shows
 * itself, so a pick never re-renders the page - which would rebuild the grid's columns and take
 * this picker down with them. What the row is to save goes into the draft the page holds instead.
 */
const ApproversEditor = ({id, draft, options, translate}: {
    id: string,
    /**
     * What the row has picked so far, which the picker both opens on and writes back to. The row
     * is virtualised and its cell is drawn again whenever the columns are rebuilt, so this picker
     * is unmounted and mounted afresh while the row is still being edited - opening it on the
     * department's saved approvers instead would drop the picks on the floor and leave Save
     * sending what the user can no longer see.
     */
    draft: MutableRefObject<DepartmentApprover[]>,
    options: DepartmentApprover[],
    translate: Translate
}) => {
    const [value, setValue] = useState<DepartmentApprover[]>(() => draft.current);

    const change = useCallback((picked: DepartmentApprover | DepartmentApprover[] | null) => {
        // the picker hands a bare approver back where it is asked for one alone, and nothing at
        // all once the last of them is taken off
        const approvers = Array.isArray(picked) ? picked : (picked ? [picked] : []);
        setValue(approvers);
        draft.current = approvers;
    }, [draft]);

    return (
        <UserSearch id={id}
                    propagateValue={change}
                    value={value}
                    options={options}
                    multi={true}
                    // Tab walks the row's controls (see onGridKeyDown) rather than picking what
                    // it lands on
                    tabSelectsValue={false}
                    menuPosition="fixed"
                    // the rows are moved about with a transform, which a menu drawn inside one
                    // would be cut off by
                    menuPortalTarget={document.body}
                    translate={translate}/>
    );
};

/**
 * ag-grid rendering of the Configuration -> Departments page, behind
 * fe-use-ng-grid-in-configuration. The classic list it replaces is in Departments.component.
 */
export const NewDepartments = (props: DepartmentsProps) => {

    const {translate} = useTranslation();
    const dispatch = useDispatch();
    // every popup this page opens is the modal router's
    const toggleModal = useModalContext()?.toggleModal;

    const currentContextId = useSelector(reduxSelectors.getUsersCurrentContextId);
    const userRoles = useSelector(reduxSelectors.getUsersRoles);
    const allDepartments: Department[] = useSelector(reduxSelectors.getDepartments);
    const filter: string = useSelector(reduxSelectors.getDepartmentsFilter);
    const possibleManagers: DepartmentApprover[] = useSelector(reduxSelectors.getPossibleDepartmentManagers);

    const [showDeletedDepartments, setShowDeletedDepartments] = useState(false);

    // the row whose approvers are open for editing. Only one row is ever in edit mode
    const [editingRowId, setEditingRowId] = useState<string | null>(null);

    /** What the picker of the row being edited has put in so far, for Save to send. */
    const draftApprovers = useRef<DepartmentApprover[]>([]);

    // the grid owns its scrolling area, so it is given the height actually left below it
    const [gridElement, setGridElement] = useState<HTMLDivElement | null>(null);
    const gridHeight = useAvailableHeight(gridElement, GRID_HEIGHT);

    const gridApi = useRef<GridApi<DepartmentGridRow> | null>(null);
    const onGridReady = useCallback((event: GridReadyEvent<DepartmentGridRow>) => {
        gridApi.current = event.api;
    }, []);

    /** The approvers the row being edited was opened with, for Leave to compare against. */
    const openedApprovers = useRef<DepartmentApprover[]>([]);

    const hasUnsavedChanges = useCallback(() => !samePicks(draftApprovers.current, openedApprovers.current), []);

    useConfirmLeaveWhileEditing(props.blocker, editingRowId !== null, hasUnsavedChanges, toggleModal, translate);

    useEffect(() => {
        dispatch(getDepartments(currentContextId));
    }, [currentContextId]); // eslint-disable-line react-hooks/exhaustive-deps -- reload only when the company changes

    const filterDepartments = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch(filterChanged(event.target.value));
    }, [dispatch]);

    const removeFilter = useCallback(() => {
        dispatch(filterChanged(''));
    }, [dispatch]);

    const toggleDeletedDepartments = useCallback((event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        event.preventDefault();
        setShowDeletedDepartments(shown => !shown);
    }, []);

    /**
     * The approvers a picker can offer. Read off a ref rather than closed over: the selector
     * behind them maps a fresh list on every call, and closing over that would rebuild the column
     * definitions - and with them the open picker - on each render.
     */
    const approverOptions = useRef(possibleManagers);
    approverOptions.current = possibleManagers;

    // the departments arrive already searched over, so the rows only drop the nameless ones and,
    // unless they are asked for, the deleted
    const rowData = useMemo(
        () => buildRowData(allDepartments, showDeletedDepartments, translate),
        [allDepartments, showDeletedDepartments, translate]
    );

    /** The row whose editor has just closed - see useReturnFocusAfterEdit. */
    const returnFocusTo = useReturnFocusAfterEdit(PAGE, editingRowId, rowData, gridApi);

    /** Leaves edit mode, dropping whatever was picked and not saved. */
    const cancelEdit = useCallback(() => {
        returnFocusTo.current = editingRowId;
        draftApprovers.current = [];
        setEditingRowId(null);
    }, [editingRowId, returnFocusTo]);

    const enterEditMode = useCallback((row: DepartmentGridRow) => {
        // the department's own approvers, to be handed back untouched wherever nothing is changed
        draftApprovers.current = row.managers;
        openedApprovers.current = row.managers;
        setEditingRowId(row.id);
    }, []);

    const saveRow = useCallback(() => {
        const rowId = editingRowId;
        const picked = draftApprovers.current;
        cancelEdit();

        const row = rowId === null ? undefined : rowData.find(candidate => candidate.id === rowId);
        if (!row) {
            return;
        }

        // redux keeps a placeholder for an approver it could not resolve, and that one has no id
        const departmentManagerIds = picked
            .filter(approver => approver.value)
            .map(approver => approver.value);

        dispatch(saveDepartmentChanges(
            {departmentId: row.internalOrgUnitId, departmentManagerIds}, currentContextId));
    }, [editingRowId, cancelEdit, rowData, dispatch, currentContextId]);

    // a row's height is measured once, so the grid has to be told to measure them again
    useEffect(() => {
        gridApi.current?.resetRowHeights();
    }, [editingRowId, rowData]);

    /**
     * The tab order inside the grid: the heading that sorts - the name alone - and then the
     * controls the rows carry, which are the Edit button of a row, and the approver picker with
     * the Save and Cancel buttons of the row being edited.
     *
     * Off the end of what the grid has drawn is rarely the end of the list: the rows are
     * virtualised, so the department to go to next is often one that is not in the document - and
     * a button the browser cannot see is one it would tab straight past, carrying focus out of
     * the grid and leaving every department below out of the keyboard's reach. The grid is asked
     * to scroll that row in, and focus follows it there once it is drawn.
     *
     * Escape gives up the row being edited - unless the picker had a list open, which is what
     * that Escape closed. The picker answers the key either way and prevents it either way, so
     * the list still standing is what tells the two apart: the first Escape closes the list, and
     * the second gives up the row.
     */
    const onGridKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === KEY.ESCAPE) {
            if (editingRowId !== null && !document.querySelector(OPEN_PICKER_LIST)) {
                cancelEdit();
            }
            return;
        }

        // a picker that has answered the key itself keeps it
        if (event.defaultPrevented) {
            return;
        }

        walkRowControls(event, ROW_CONTROLS, (target, step) => {
            /*
             * Only while nothing is being edited. The Edit button of every other row is disabled
             * for as long as one row holds the editor (see RowActions below), so the row this
             * would carry focus to has nothing in it to land on - and a disabled button takes no
             * focus. Answering the key would prevent it and leave focus where it stood; left
             * alone, the last control of the editor hands it to whatever follows the grid.
             */
            if (editingRowId !== null) {
                return false;
            }

            const api = gridApi.current;
            const from = rowIndexOf(target);
            const next = api && from !== null ? rowToFocus(api, from + step, step) : null;

            if (api && next?.data && next.rowIndex !== null) {
                api.ensureIndexVisible(next.rowIndex);
                focusWhenDrawn(editButtonId(PAGE, next.data.id));
                return true;
            }
            return false;
        });
    }, [editingRowId, cancelEdit]);

    const columnDefs = useMemo<ColDef<DepartmentGridRow>[]>(() => {
        const nameLabel = translate('departments.departments');
        const approversLabel = translate('departments.departmentApprovers');
        // only a system administrator assigns approvers; everyone else reads the list
        const readOnly = !userRoles.systemAdministrator;
        const cellOwnsKey = cellOwnsKeysWhileEditing<DepartmentGridRow>(editingRowId);

        return [gridColumn<DepartmentGridRow>({
            colId: NAME_COL,
            field: 'name',
            headerName: nameLabel,
            // the proportions the classic list gave its two columns with col-md-4 / col-md-8
            flex: 4,
            minWidth: 160,
            // the list arrives ordered by name, and the third click on the heading returns it there
            sortable: true,
            suppressHeaderKeyboardEvent: tabOutOfTheHeaderIsNotTheGrids,
            // ag-grid cannot spot truncation inside a cell renderer, so the tooltip is asked for
            // explicitly
            tooltipValueGetter: params => params.data?.label || undefined,
            cellRenderer: (params: ICellRendererParams<DepartmentGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                return (
                    <span className={row.deleted ? 'new-departments__name text-danger' : 'new-departments__name'}>
                        {row.label}
                    </span>
                );
            }
        }), gridColumn<DepartmentGridRow>({
            colId: APPROVERS_COL,
            headerName: approversLabel,
            flex: 8,
            minWidth: 220,
            // the cell reads as a list of names, and the departments arrive ordered by their own:
            // there is nothing here to order the rows by
            sortable: false,
            // The cell carries the row's height, either way it is read: the picker it holds while
            // the row is edited grows with the approvers on it, and the names it reads as
            // otherwise wrap onto a line each rather than being clipped to one. A department with
            // few of them is held at the height of every other row by the stylesheet's floor.
            autoHeight: true,
            wrapText: true,
            valueGetter: (params: ValueGetterParams<DepartmentGridRow>) => params.data?.approvers,
            tooltipValueGetter: params => params.data?.approvers || undefined,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<DepartmentGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }
                if (row.id !== editingRowId) {
                    return <span className="new-departments__approvers"><ApproverList managers={row.managers}/></span>;
                }
                const id = pickerId(row.id);
                return (
                    <span className="new-departments__editor new-departments__editor--approvers"
                          data-cy="departments.approvers">
                        {/* the picker is one cell of a grid, so it says which department it belongs to */}
                        <label className="new-departments__picker-label" htmlFor={id}>
                            {`${approversLabel} - ${row.name}`}
                        </label>
                        <ApproversEditor id={id}
                                         draft={draftApprovers}
                                         options={approverOptions.current}
                                         translate={translate}/>
                    </span>
                );
            }
        }), actionsColumn<DepartmentGridRow>({
            headerName: translate('departments.actions'),
            width: EDITING_ACTIONS_WIDTH,
            suppressKeyboardEvent: cellOwnsKey,
            cellRenderer: (params: ICellRendererParams<DepartmentGridRow>) => {
                const row = params.data;
                if (!row) {
                    return null;
                }

                if (row.id === editingRowId) {
                    return (
                        <span className="app-grid__actions">
                            {/* the labels the other configuration rows wear - departments has none of its own */}
                            <Button type={ButtonType.SECONDARY}
                                    onClick={saveRow}
                                    text={translate('roles.save')}
                            />
                            <Button type={ButtonType.SECONDARY}
                                    onClick={cancelEdit}
                                    text={translate('roles.cancel')}
                            />
                        </span>
                    );
                }

                // a department the master data has deleted has nothing left to assign
                if (row.deleted) {
                    return null;
                }

                return (
                    <RowActions page={PAGE}
                                rowId={row.id}
                                // one row is edited at a time, as the other configuration lists have it
                                disabled={readOnly || editingRowId !== null}
                                onEdit={() => enterEditMode(row)}
                                translate={translate}/>
                );
            }
        })];
    }, [translate, userRoles.systemAdministrator, editingRowId, saveRow, cancelEdit, enterEditMode]);

    return (
        <ConfigurationPage page={PAGE}
                           titleKey="departments.header"
                           infoKey="departments.headerInfo"
                           translate={translate}>

            <div className="d-flex justify-content-end gap-3 align-items-center mb-24">
                {/* the Checkbox label carries w-100, so it needs a shrink-to-fit wrapper to not stretch the row */}
                <div className="d-flex align-items-center text-nowrap">
                    <Checkbox
                        inputId={"showDeletedDepartments"}
                        onChange={toggleDeletedDepartments}
                        isChecked={showDeletedDepartments}
                        ariaLabel={translate("departments.showDeleted")}
                        label={translate("departments.showDeleted")}
                    />
                </div>
                <SearchGroupInput onInputChange={filterDepartments}
                                  value={filter}
                                  className="ga-input__default"
                                  onClearInput={removeFilter}
                                  translate={translate}/>
            </div>

            <AppGrid<DepartmentGridRow>
                className="new-departments__grid"
                dataCy="departments.grid"
                containerRef={setGridElement}
                height={gridHeight}
                rowData={rowData}
                columnDefs={columnDefs}
                getRowId={(params: GetRowIdParams<DepartmentGridRow>) => params.data.id}
                rowHeight={ROW_HEIGHT}
                onGridReady={onGridReady}
                onKeyDown={onGridKeyDown}
                localeText={{noRowsToShow: translate('departments.noMatches')}}
            />
        </ConfigurationPage>
    );
};

/**
 * Configuration -> Departments. The flag is read here rather than around the route, so the page
 * itself decides which of its two renderings it is.
 */
export const DepartmentsWrapper = (props: DepartmentsProps) =>
    isAgGridInConfigurationEnabled() ? <NewDepartments {...props}/> : <Departments {...props}/>;

export default DepartmentsWrapper;
